'use client';

import { useAttachmentTextContent } from '@commenda/api/commenda-ai/useAttachmentTextContent';
import { useAttachmentViewUrl } from '@commenda/api/commenda-ai/useAttachmentViewUrl';
import { DocViewerComponent, Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { formatAttachmentName, getAttachmentKind } from './attachment-kind';

export interface PreviewFile {
  fileName: string;
  mimeType: string;
  /** Stored attachment reference (raw or already-signed URL); resolved to a fresh viewable URL here. */
  fileRef: string;
  /** Forced-download URL. */
  downloadUrl?: string;
}

const HEADER_ICON_BUTTON =
  'flex shrink-0 cursor-pointer items-center justify-center rounded-sm p-xs border-none bg-transparent text-fg-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary';

/** DocViewer has no renderer for .txt/.md (it would fall back to the Office viewer, which can't open
 * them), so fetch and show the raw text. */
function TextPreview({ url }: { url: string }) {
  const { text, isLoading, error } = useAttachmentTextContent(url);

  if (isLoading) {
    return (
      <p className="p-2 txt-small-regular text-text-quaternary">
        Loading preview…
      </p>
    );
  }
  if (error) {
    return (
      <p className="p-2 txt-small-regular text-text-quaternary">
        Couldn&apos;t load a preview — use Download to open the file.
      </p>
    );
  }
  return (
    <pre className="whitespace-pre-wrap break-words bg-bg-primary p-4 font-mono text-xs text-text-primary">
      {text}
    </pre>
  );
}

/** Document preview panel (Figma 61:22274): a bordered card with a breadcrumb header (name · format),
 * Download, a full-screen toggle and close, over the rendered file. Uses the app's DocViewer for
 * pdf/csv/Office, a native <img> for images, and a text view for plain text/markdown. Placement +
 * width are owned by the layout via `className`. */
export function AttachmentPreviewPanel({
  file,
  onClose,
  isFullscreen,
  onToggleFullscreen,
  className,
}: {
  file: PreviewFile;
  onClose: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  className?: string;
}) {
  const { kind, label } = getAttachmentKind(
    file.mimeType,
    file.fileName,
    file.fileRef
  );
  const { url, isLoading, error } = useAttachmentViewUrl(file.fileRef);

  return (
    <aside
      className={cn(
        'flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-border-secondary bg-bg-primary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)]',
        className
      )}
    >
      <header className="flex h-12 shrink-0 items-center gap-xl border-b-[0.5px] border-border-primary px-lg py-md">
        {/* Breadcrumb: name · format */}
        <div className="flex min-w-0 flex-1 items-center gap-sm">
          <span className="truncate txt-small-medium text-text-primary">
            {formatAttachmentName(file.fileName)}
          </span>
          <span
            className="size-[3px] shrink-0 rounded-full bg-fg-quaternary"
            aria-hidden="true"
          />
          <span className="shrink-0 txt-extra-small-regular text-text-secondary">
            {label}
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-lg">
          {file.downloadUrl && (
            <a
              href={file.downloadUrl}
              download={file.fileName}
              className="flex shrink-0 cursor-pointer items-center justify-center rounded-sm border-[0.5px] border-border-primary bg-bg-primary px-md py-sm txt-small-medium text-text-secondary shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)] transition-colors hover:text-text-primary"
            >
              Download
            </a>
          )}
          <div className="flex shrink-0 items-center gap-sm">
            <button
              type="button"
              aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
              onClick={onToggleFullscreen}
              className={HEADER_ICON_BUTTON}
            >
              {isFullscreen ? (
                <Icons.Minimize01 className="size-4" aria-hidden="true" />
              ) : (
                <Icons.Maximize01 className="size-4" aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              aria-label="Close preview"
              onClick={onClose}
              className={HEADER_ICON_BUTTON}
            >
              <Icons.XClose className="size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto bg-bg-secondary">
        {isLoading || !url ? (
          <p className="p-2 txt-small-regular text-text-quaternary">
            {error
              ? "Couldn't load a preview — use Download to open the file."
              : 'Loading preview…'}
          </p>
        ) : kind === 'text' ? (
          <TextPreview url={url} />
        ) : kind === 'image' ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img alt={file.fileName} src={url} className="mx-auto max-w-full" />
        ) : (
          <div className="h-full w-full">
            <DocViewerComponent
              uri={url}
              fillContainer
              styles={{
                height: '100%',
                minHeight: '100%',
                borderRadius: '0px',
              }}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
